import React, {useReducer} from 'react';
import {Context} from './Context';
import {initialState, reducer} from './Reducer';
import {IContext, ACTION_VERBS} from './Types';
import useUserContextState from '../user/Context';
import * as apiService from '../../services';
import {
  IBalanceSheetResponse,
  IClientListResponse,
  IDebtorListResponse,
  IHistoryResponse,
  IOrderDetailResponse,
  IProductListResponse,
  IReceivableListResponse,
} from '../../interfaces/services';
import {IStandardServiceResponse} from '../../interfaces/services/standard-service.interface';
import {
  IAgropecuaryClient,
  IBalanceSheetItem,
  ICandidateOrderItem,
  IClient,
  IDebtorItem,
  INewClientRequest,
  IOrderDetail,
  IOrderItemDto,
  IPaymentProof,
  IReceivable,
  ISalesmanClient,
  IScheduleItem,
  ISemaphoreValue,
  ITaxProof,
} from '../../interfaces/dtos';
import {handleError} from '../../utils/exceptions/handle-error';

import {INewClientDocumentRequest} from '../../interfaces/dtos/new-client-request.interface';
import {IDocumentClient} from '../../interfaces/dtos/salesman-client.interface';
import {IProfile} from '../user/Types';
import {
  ICategory,
  IListOption,
  IOrder,
  IOrderItem,
  IPortfolioFilter,
  IProduct,
  IStatusFilter,
} from '../commons/interfaces';
import {ISalesmanResourceResponse} from '../../interfaces/services/salesman-resources.interface';
import {IBalanceListResponse} from '../../interfaces/services/balances.interface';
import {ISemaphoreResponse} from '../../interfaces/services/semaphore-response.interface';
import {IPaymentForm, ITaxForm} from '../../interfaces/forms';
import {ProofType} from '../../constants/enumerations';
import {IOrderListResponse} from '../../interfaces/services/order-list-response.interface';
import {IOrderRequest} from '../../interfaces/services/order-request.interface';
import {IResponse} from '../../interfaces/services/response.interface';
import {delay} from '../../utils/async/delay';
import IInvoiceCandidate from '../../interfaces/dtos/invoice-candidate-list.interface';
import {IInvoiceRequest} from '../../interfaces/dtos/invoice-request.interface';
import {IInvoiceItem} from '../../interfaces/dtos/invoice-item.interface';
import {
  SALESMAN_REQUEST_TO_SYNC_KEY,
  clearFromStorage,
  loadFromStorage,
  saveToStorage,
} from '../../utils/storage/local-storage';
import {ERROR_CONNECTION_FAILURE} from '../../utils/exceptions';
import {WithoutConnectionWorkflowType} from '../../interfaces/types/without-connection-workflow.type';
import {
  RESULT_REQUEST_CACHED_FAILED,
  RESULT_REQUEST_CACHED_OK,
  RESULT_REQUEST_CACHED_ON_STORAGE,
  ResultOfRequestCached,
} from '../../interfaces/types/result-of-request-cached';
import {IRequestsToSyncItem} from '../../interfaces/dtos/requests-to-sync.interface';
import {IProofPaymentRequest} from '../../interfaces/dtos/proof-payment-request.interface';
import {
  IOrderCandidate,
  IOrderCandidateResponse,
} from '../../interfaces/services/candidate-order-detail-response.interface';
import {responseFailedError} from '../../utils/exceptions/exceptions';

/**
 *
 * @param props
 * @returns
 */
export const SalesmanProvider = (props: any) => {
  const {
    CLEAR_ERRORS,
    STATE_PROCESS_ERROR,
    GET_SALESMAN_CLIENTS,
    SALESMAN_NEW_CLIENT_REQUEST,
    SEND_SALESMAN_NEW_CLIENT_REQUEST,
    SET_SALESMAN_PRODUCTS,
    SET_SALESMAN_PRODUCT_TO_ADD,
    UPDATE_SALESMAN_NEW_ORDER,
    UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART,
    SEND_SALESMAN_NEW_ORDER,
    SET_SALESMAN_CLIENT,
    FETCH_BALANCE_CLIENT,
    BALANCE_SHEET,
    SALESMAN_RESOURCES,
    CLIENT_HISTORY_LIST,
    CUSTOMER_BALANCE_LIST,
    SEMAPHORE_ITEMS,
    DEBTOR_LIST,
    RECEIVABLE_LIST,
    SET_PORTFOLIO_FILTER,
    GET_SALESMAN_ORDER_LIST,
    GET_SALESMAN_ORDER_DETAIL,
    SET_ORDER_FILTER,
    SET_SCHEDULE,
    FETCH_SCHEDULE,
    SET_APPOINTMENT,
    FETCH_INVOICE_CANDIDATES,
    SET_INVOICE_CANDIDATE,
    SET_INVOICE_REQUEST,
    SET_INVOICE_ITEMS_EDITING,
    SEND_SALESMAN_INVOICE_REQUEST,
    RETRIEVE_REQUESTS_TO_SERVER,
    CLEAR_REQUESTS_TO_SERVER,
    UPDATED_REQUESTS_SAVED_TO_STORAGE,
    SYNC_NEW_CLIENT_REQUEST,
    SEND_SALESMAN_NEW_PROOF_OF_PAYMENT,
    SYNC_PROOF_OF_PAYMENT_REPORT_REQUEST,
    SYNC_SALES_ORDER_REQUEST,
    FETCH_ORDER_CANDIDATES,
    FETCH_CANDIDATE_ORDER_DETAIL,
  } = ACTION_VERBS;

  const {getToken, profile, isConnected, setRemoteMessageError} =
    useUserContextState();

  const [state, dispatch] = useReducer(reducer, initialState);

  const prepareDate = (date: Date | undefined) => {
    if (date) {
      return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(
        2,
        '0',
      )}${String(date.getUTCDate()).padStart(2, '0')}`;
    }

    return '';
  };

  const preparePaymentProof = (form: IPaymentForm): IPaymentProof => {
    return {
      amount: form.amount as number,
      bank: form.bank as string,
      date: prepareDate(form.date),
      file: {
        name: form.file ? form.file.name : '',
        data: form.file && form.file.content ? form.file.content : '',
      },
      voucher: form.voucher as string,
      comments: form.comments ?? '',
    };
  };

  const prepareTaxProof = (form: IPaymentForm): ITaxProof => {
    return {
      amount: form.amount as number,
      date: prepareDate(form.date),
      file: {
        name: form.file ? form.file.name : '',
        data: form.file && form.file.content ? form.file.content : '',
      },
      voucher: form.voucher as string,
      comments: form.comments ?? '',
    };
  };

  const getReceivableList = (
    response: IReceivableListResponse,
  ): IReceivable[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const parseSchedulePayload = (response: IResponse): IScheduleItem[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const parseDebtorList = (response: IDebtorListResponse): IDebtorItem[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getSemaphoreItems = (
    response: ISemaphoreResponse,
  ): ISemaphoreValue[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getBalanceList = (response: IBalanceListResponse) => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getHistory = (response: IHistoryResponse) => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getSalesmanTerritoriesAtLogin = (payload: any): IListOption[] => {
    if (payload.salesTerritories) {
      return payload.salesTerritories as IListOption[];
    }
    return [];
  };

  const extractCustomerTypes = (payload: any): IListOption[] => {
    if (payload.customerTypes) {
      return payload.customerTypes as IListOption[];
    }
    return [];
  };

  const extractRequiredDocumentList = (payload: any): IListOption[] => {
    if (payload.requiredDocumentList) {
      return payload.requiredDocumentList as IListOption[];
    }
    return [];
  };

  const extractPaymentOptions = (payload: any): IListOption[] => {
    if (payload.paymentOptions) {
      return payload.paymentOptions as IListOption[];
    }
    return [];
  };

  const extractCategories = (payload: any): ICategory[] => {
    if (payload.salesCategories) {
      return payload.salesCategories as ICategory[];
    }
    return [];
  };

  const extractCities = (payload: any): IListOption[] => {
    if (payload.salesCities) {
      return payload.salesCities as IListOption[];
    }
    return [];
  };

  const extracRequiredDocumentsHelp = (payload: any): string => {
    if (payload.requiredDocumentsHelp) {
      return payload.requiredDocumentsHelp;
    }
    return '';
  };

  const getBalanceSheet = (
    response: IBalanceSheetResponse,
  ): IBalanceSheetItem | null => {
    if (response.payload) {
      return response.payload[0];
    }
    return null;
  };

  const getClientList = (response: IClientListResponse): IClient[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getProductList = (response: IProductListResponse): IProduct[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getSalesmanCandidateInvoiceList = (
    response: IResponse,
  ): IInvoiceCandidate[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getSalesmanCandidateOrderList = (
    response: IResponse,
  ): ICandidateOrderItem[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getCandidateOrderDetail = (
    response: IOrderCandidateResponse,
  ): IOrderCandidate | undefined => {
    if (response.payload) {
      return response.payload;
    }
    return undefined;
  };

  const setSalesmanNewClientRequest = (request: ISalesmanClient) => {
    dispatch({
      type: SALESMAN_NEW_CLIENT_REQUEST,
      payload: request,
    });
  };

  const fetchClients = async (): Promise<boolean> => {
    try {
      const response: IClientListResponse = await apiService.getSalesmanClients(
        getToken(),
      );

      if (response.success) {
        const clients = getClientList(response);
        dispatch({
          type: GET_SALESMAN_CLIENTS,
          payload: clients,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, GET_SALESMAN_CLIENTS);
    }
    return false;
  };

  const fetchSalesmanProducts = async (
    priceListId?: string,
    paymentOptionId?: string,
  ): Promise<boolean> => {
    try {
      const response: IProductListResponse =
        await apiService.getSalesmanProducts(
          priceListId,
          paymentOptionId,
          getToken(),
        );

      if (response.success) {
        const products = getProductList(response);
        dispatch({
          type: SET_SALESMAN_PRODUCTS,
          payload: products,
        });
        return true;
      }
    } catch (error) {
      handleError(
        error,
        STATE_PROCESS_ERROR,
        'fetchSalesmanProducts'.toUpperCase(),
      );
    }
    return false;
  };

  const setSalesmanProductToAdd = (product: IProduct | null) => {
    dispatch({
      type: SET_SALESMAN_PRODUCT_TO_ADD,
      payload: product,
    });
  };

  const buildNewClientRequest = (
    documentList: IDocumentClient[],
  ): INewClientRequest => {
    const {salesmanNewClientRequest} = state;
    const {
      identity,
      legalName,
      address,
      phone,
      zoneId,
      clientTypeId,
      salesman,
      businessHours,
      deliveryHours,
      transportTypes,
      deliveryConstrains,
      geoLocation,
    } = salesmanNewClientRequest as ISalesmanClient;

    const {insaiCode, sunagroCode} =
      salesmanNewClientRequest as IAgropecuaryClient;

    const documents: INewClientDocumentRequest[] = documentList
      ? documentList.map(item => {
          return {
            name: item.document.name,
            data: item.document.content as string,
            documentType: item.documentType,
          };
        })
      : [];

    return {
      identity,
      legalName,
      address,
      phone,
      zoneId,
      clientTypeId,
      salesman: salesman as string,
      profile: {insaiCode, sunagroCode},
      documents,
      businessHours,
      deliveryHours,
      transportTypes,
      deliveryConstrains,
      geoLocation,
    };
  };

  const sendSalesmanNewClientRequest = async (
    documents: IDocumentClient[],
  ): Promise<ResultOfRequestCached> => {
    let response: IStandardServiceResponse;
    const token = getToken();
    let request = null;
    try {
      request = buildNewClientRequest(documents);
      if (!isConnected()) {
        return {status: 'ok'};
      }
      response = await apiService.postNewClientRequest(request, token);

      if (response.success) {
        dispatch({
          type: SEND_SALESMAN_NEW_CLIENT_REQUEST,
        });
        return RESULT_REQUEST_CACHED_OK;
      }

      handleError(
        responseFailedError,
        STATE_PROCESS_ERROR,
        SEND_SALESMAN_NEW_CLIENT_REQUEST,
      );
    } catch (e) {
      const error = e as Error;
      if (error.message === ERROR_CONNECTION_FAILURE) {
        return {
          ...RESULT_REQUEST_CACHED_ON_STORAGE,
          request: request ? request : undefined,
        };
      }
      handleError(e, STATE_PROCESS_ERROR, SEND_SALESMAN_NEW_CLIENT_REQUEST);
    }
    return RESULT_REQUEST_CACHED_FAILED;
  };

  /**
   *
   */
  const clearErrors = () => {
    dispatch({
      type: CLEAR_ERRORS,
    });
  };

  const createSalesmanNewOrder = (client: IClient) => {
    const order: IOrder = {
      id: null,
      client,
      items: [],
      transport: null,
      date: new Date(),
      paymentOptionId: null,
      foreign: false,
      priceListId: null,
    };
    updateSalesmanNewOrder(order);
  };

  const updateSalesmanNewOrder = (order: IOrder | null) => {
    dispatch({
      type: UPDATE_SALESMAN_NEW_ORDER,
      payload: order,
    });
  };

  const resetNewOrder = () => {
    if (state.customerOrder && state.customerOrder.client) {
      createSalesmanNewOrder(state.customerOrder?.client);
    }
  };

  const updateCandidateToShoppingCart = (item: IOrderItem | null) => {
    dispatch({
      type: UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART,
      payload: item,
    });
  };

  const sendSalesmanNewOrder = async (
    order: IOrder,
  ): Promise<ResultOfRequestCached> => {
    const token = getToken();
    let items: IOrderItemDto[] = order.items!.map(el => {
      const {
        count,
        price,
        product: {id: productId},
        unit: {id: unitId},
      } = el;
      //
      return {
        count,
        productId,
        unitId,
        price: price,
      };
    });
    const {
      client: {id: clientId},
      date,
      transport,
      paymentOptionId,
      foreign,
      priceListId,
      requestTransportQuote,
    } = order;

    const transportPayload = transport
      ? {transport, requestTransportQuote: false}
      : {requestTransportQuote: requestTransportQuote ?? false};

    const request: IOrderRequest = {
      clientId,
      clientName: state.client ? state.client?.name! : '',
      items,
      date: date ? date?.toString() : null,
      paymentOptionId,
      foreign,
      ...transportPayload,
      priceListId,
    };

    try {
      const response: IStandardServiceResponse = await apiService.postNewOrder(
        request,
        token,
      );
      if (response.success) {
        dispatch({
          type: SEND_SALESMAN_NEW_ORDER,
          payload: order,
        });
        return RESULT_REQUEST_CACHED_OK;
      }
    } catch (e) {
      const error = e as Error;
      if (error.message === ERROR_CONNECTION_FAILURE) {
        return {...RESULT_REQUEST_CACHED_ON_STORAGE, request};
      }
      handleError(error, STATE_PROCESS_ERROR, SEND_SALESMAN_NEW_ORDER);
    }
    return RESULT_REQUEST_CACHED_FAILED;
  };

  const setSalesmanClient = (client: IClient | null) => {
    dispatch({
      type: SET_SALESMAN_CLIENT,
      payload: client,
    });
    return true;
  };

  const fetchBalanceCustomer = async (client: IClient): Promise<boolean> => {
    try {
      const response: IBalanceSheetResponse =
        await apiService.getSubclientBalanceSheet(client, getToken());
      if (response.success) {
        const balance = getBalanceSheet(response);
        dispatch({
          type: FETCH_BALANCE_CLIENT,
          payload: balance,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, FETCH_BALANCE_CLIENT);
    }
    return false;
  };

  const getProfile = (): IProfile | null => {
    return profile;
  };

  /**
   *
   * @returns
   */
  const fetchBalanceSheet = async (
    filter?: IPortfolioFilter | null,
  ): Promise<boolean> => {
    try {
      const response: IBalanceSheetResponse =
        await apiService.getSalesmanBalanceSheet(getToken(), filter);
      if (response.success) {
        const balance = getBalanceSheet(response);
        dispatch({
          type: BALANCE_SHEET,
          payload: balance,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, BALANCE_SHEET);
    }
    return false;
  };

  const fetchResources = async (): Promise<boolean> => {
    try {
      const response: ISalesmanResourceResponse =
        await apiService.getSalesmanResources(getToken());
      if (response.success) {
        const salesTerritories = getSalesmanTerritoriesAtLogin(
          response.payload,
        );

        const customerTypes = extractCustomerTypes(response.payload);

        const customerRequiredDocumentList = extractRequiredDocumentList(
          response.payload,
        );

        const customerRequiredDocumentsHelp = extracRequiredDocumentsHelp(
          response.payload,
        );

        const customerPaymentOptions = extractPaymentOptions(response.payload);

        const salesCategories = extractCategories(response.payload);
        const salesCities = extractCities(response.payload);

        const payload: any = {
          salesTerritories,
          customerTypes,
          customerRequiredDocumentList,
          customerPaymentOptions,
          customerRequiredDocumentsHelp,
          salesCategories,
          salesCities,
        };

        dispatch({
          type: SALESMAN_RESOURCES,
          payload,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SALESMAN_RESOURCES);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchHistoryList = async (): Promise<boolean> => {
    try {
      const {client} = state;
      if (client) {
        const response: IHistoryResponse = await apiService.getSubClientHistory(
          client,
          getToken(),
        );

        if (response.success) {
          const history = getHistory(response);
          dispatch({
            type: CLIENT_HISTORY_LIST,
            payload: history,
          });
          return true;
        }
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, CLIENT_HISTORY_LIST);
    }
    return false;
  };

  /**
   *
   * @returns
   */
  const fetchBalanceList = async (): Promise<boolean> => {
    try {
      const {client} = state;
      if (client) {
        const response: IBalanceListResponse =
          await apiService.getSubClientBalanceList(client, getToken());

        if (response.success) {
          const balances = getBalanceList(response);

          dispatch({
            type: CUSTOMER_BALANCE_LIST,
            payload: balances,
          });
          return true;
        }
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, CUSTOMER_BALANCE_LIST);
    }
    return false;
  };

  const fetchSemaphoreItems = async (
    filters?: IPortfolioFilter | null,
  ): Promise<boolean> => {
    try {
      const response: ISemaphoreResponse =
        await apiService.getSalesmanSemphoreItems(getToken(), filters);

      if (response.success) {
        const semaphoreItems = getSemaphoreItems(response);
        dispatch({
          type: SEMAPHORE_ITEMS,
          payload: semaphoreItems,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEMAPHORE_ITEMS);
    }
    return false;
  };

  const fetchDebtorList = async (
    key: string,
    filter?: IPortfolioFilter | null,
  ): Promise<boolean> => {
    try {
      const response: IDebtorListResponse =
        await apiService.getSalesmanDebtorsList(key, getToken(), filter);

      if (response.success) {
        const documents = parseDebtorList(response);
        dispatch({
          type: DEBTOR_LIST,
          payload: documents,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, DEBTOR_LIST);
    }
    return false;
  };

  const fetchReceivableListByDebtor = async (
    key: string,
    rif: string,
  ): Promise<boolean> => {
    try {
      const response: IReceivableListResponse =
        await apiService.getSalesmanReceivablesByDebtor(getToken(), key, rif);

      if (response.success) {
        const payload = getReceivableList(response);
        dispatch({
          type: RECEIVABLE_LIST,
          payload,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, RECEIVABLE_LIST);
    }
    return false;
  };

  const sendProof = async (
    form: IPaymentForm | ITaxForm,
    type: ProofType,
  ): Promise<ResultOfRequestCached> => {
    let response: IStandardServiceResponse;
    const token = getToken();
    const {client} = state;

    const proof =
      type === ProofType.payment
        ? preparePaymentProof(form as IPaymentForm)
        : prepareTaxProof(form as IPaymentForm);

    const request: IProofPaymentRequest = {proof, type, client};
    try {
      if (client) {
        response = await apiService.postPaymentProof(request, token);

        if (response.success) {
          dispatch({
            type: SEND_SALESMAN_NEW_PROOF_OF_PAYMENT,
          });
          return RESULT_REQUEST_CACHED_OK;
        } else if (
          response.error &&
          response.error.code === '422' &&
          response.error.message
        ) {
          setRemoteMessageError(response.error.message);
        }
      }
    } catch (e) {
      const error = e as Error;
      if (error.message === ERROR_CONNECTION_FAILURE) {
        return {...RESULT_REQUEST_CACHED_ON_STORAGE, request};
      }
      handleError(e, STATE_PROCESS_ERROR, SEND_SALESMAN_NEW_PROOF_OF_PAYMENT);
    }
    return RESULT_REQUEST_CACHED_FAILED;
  };

  const setPortfolioFilter = (filter: IPortfolioFilter | null) => {
    dispatch({
      type: SET_PORTFOLIO_FILTER,
      payload: filter,
    });
  };

  const fetchSalesmanOrderList = async (): Promise<boolean> => {
    try {
      const response: IOrderListResponse =
        await apiService.getSalesmanOrderList(
          getToken(),
          state.salesmanOrderFilter,
        );

      if (response.success) {
        dispatch({
          type: GET_SALESMAN_ORDER_LIST,
          payload: response.payload,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, GET_SALESMAN_ORDER_LIST);
    }
    return false;
  };

  const fetchSalesmanOrderDetail = async (id: string): Promise<boolean> => {
    try {
      const response: IOrderDetailResponse =
        await apiService.getSalesmanOrderDetail(id, getToken());

      if (response.success) {
        setSalesmanOrderDetail(response.payload);
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, GET_SALESMAN_ORDER_DETAIL);
    }
    return false;
  };

  const setSalesmanOrderDetail = (detail: IOrderDetail | null) => {
    dispatch({
      type: GET_SALESMAN_ORDER_DETAIL,
      payload: detail,
    });
  };

  const setSalesmanOrderFilter = (filter: IStatusFilter | null) => {
    dispatch({type: SET_ORDER_FILTER, payload: filter});
  };

  const clearSalesmanProducts = () => {
    dispatch({type: SET_SALESMAN_PRODUCTS, payload: null});
  };

  const setSchedule = (items: IScheduleItem[]) => {
    dispatch({type: SET_SCHEDULE, payload: items});
  };

  const fecthSchedule = async (): Promise<boolean> => {
    try {
      const response: IResponse = await apiService.retrieveSchedule(getToken());
      if (response.success) {
        setSchedule(parseSchedulePayload(response));
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, FETCH_SCHEDULE);
    }
    return false;
  };

  const sendAppointConfirmation = async (code: string): Promise<boolean> => {
    try {
      if (state.appointment) {
        const response: IResponse = await apiService.postAppointConfirmation(
          code,
          state.appointment?.id,
          getToken(),
        );
        if (response.success) {
          return true;
        }
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, FETCH_SCHEDULE);
    }
    return false;
  };

  const setAppointment = (appointment: IScheduleItem | null) => {
    dispatch({type: SET_APPOINTMENT, payload: appointment});
  };

  const fetchInvoiceCandidates = async (): Promise<boolean> => {
    try {
      const {client} = state;
      if (client) {
        const response: IResponse =
          await apiService.getSalesmanInvoiceCandidateList(
            client.id,
            getToken(),
          );

        const dispatched = {
          type: FETCH_INVOICE_CANDIDATES,
          payload: getSalesmanCandidateInvoiceList(response),
        };
        dispatch(dispatched);
        await delay(1000);
        if (response.success && dispatched.payload.length >= 0) {
          return true;
        }
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, FETCH_SCHEDULE);
    }

    return false;
  };

  const setInvoiceCandidate = async (candidate: IInvoiceCandidate) => {
    dispatch({
      type: SET_INVOICE_CANDIDATE,
      payload: candidate,
    });
  };

  const setInvoiceRequest = async (request: IInvoiceRequest) => {
    dispatch({
      type: SET_INVOICE_REQUEST,
      payload: request,
    });
  };

  const setInvoiceItemEditing = async (request: IInvoiceItem | null) => {
    dispatch({
      type: SET_INVOICE_ITEMS_EDITING,
      payload: request,
    });
  };

  const sendInvoiceRequest = async (): Promise<boolean> => {
    try {
      if (state.invoiceRequest) {
        const response: IResponse = await apiService.postInvoiceRequest(
          state.invoiceRequest,
          getToken(),
        );
        if (response.success) {
          dispatch({
            type: SEND_SALESMAN_INVOICE_REQUEST,
            payload: true,
          });
          await delay(1000);
          return true;
        }
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEND_SALESMAN_NEW_ORDER);
    }
    return false;
  };

  /*    ,
    RETRIEVE_REQUESTS_TO_SERVER,
    CLEAR_REQUESTS_TO_SERVER,*/

  const saveRequestToServer = async (
    workflow?: WithoutConnectionWorkflowType,
    value?: INewClientRequest | IProofPaymentRequest | IOrderRequest,
  ): Promise<boolean> => {
    if (workflow && value) {
      if (workflow === 'customer-registration') {
        state.requestsToSync?.customerRegistryList.push({
          request: value,
          createdAt: new Date(),
          workflow,
        });
      }

      if (workflow === 'payment-proof') {
        state.requestsToSync?.paymentReportList.push({
          request: value,
          createdAt: new Date(),
          workflow,
        });
      }

      if (workflow === 'salesman-order') {
        state.requestsToSync?.salesOrderList.push({
          request: value,
          createdAt: new Date(),
          workflow,
        });
      }

      dispatch({
        type: UPDATED_REQUESTS_SAVED_TO_STORAGE,
        payload: state.requestsToSync,
      });

      return await updateAllRequestToStorage();
    }
    return false;
  };

  const updateAllRequestToStorage = async (): Promise<boolean> => {
    try {
      await saveToStorage(
        SALESMAN_REQUEST_TO_SYNC_KEY,
        state.requestsToSync,
        true,
      );

      return true;
    } catch (error) {
      handleError(
        error,
        STATE_PROCESS_ERROR,
        UPDATED_REQUESTS_SAVED_TO_STORAGE,
      );
    }
    return false;
  };

  const retrieveRequestToServer = async (): Promise<boolean> => {
    try {
      const storageData = await loadFromStorage(
        SALESMAN_REQUEST_TO_SYNC_KEY,
        true,
      );
      dispatch({
        type: RETRIEVE_REQUESTS_TO_SERVER,
        payload: storageData ?? initialState.requestsToSync,
      });
      await delay(1000);
      return true;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, RETRIEVE_REQUESTS_TO_SERVER);
    }
    return false;
  };

  const clearRequestToServer = async (): Promise<boolean> => {
    try {
      await clearFromStorage(SALESMAN_REQUEST_TO_SYNC_KEY);
      dispatch({
        type: CLEAR_REQUESTS_TO_SERVER,
        payload: null,
      });
      return true;
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, RETRIEVE_REQUESTS_TO_SERVER);
    }
    return false;
  };

  const hasRequestsToSinchronize = (): boolean => {
    const {requestsToSync} = state;

    if (requestsToSync) {
      return (
        requestsToSync.invoiceList.length > 0 ||
        requestsToSync.paymentReportList.length > 0 ||
        requestsToSync.customerRegistryList.length > 0 ||
        requestsToSync.salesOrderList.length > 0
      );
    }
    return false;
  };

  const syncNewClientRequest = async (
    toSync: IRequestsToSyncItem,
  ): Promise<boolean> => {
    const token = getToken();
    try {
      const response = await apiService.postNewClientRequest(
        toSync.request as INewClientRequest,
        token,
        false,
      );

      if (response && response.success) {
        dispatch({
          type: SYNC_NEW_CLIENT_REQUEST,
          payload: toSync.createdAt,
        });
        return true;
      }
    } catch (ex) {
      handleError(ex, STATE_PROCESS_ERROR, SYNC_NEW_CLIENT_REQUEST);
    }
    return false;
  };

  const syncPaymentProofRequest = async (
    toSync: IRequestsToSyncItem,
  ): Promise<boolean> => {
    const token = getToken();
    try {
      const response = await apiService.postPaymentProof(
        (toSync as any as IRequestsToSyncItem).request as IProofPaymentRequest,
        token,
        false,
      );
      if (response && response.success) {
        dispatch({
          type: SYNC_PROOF_OF_PAYMENT_REPORT_REQUEST,
          payload: (toSync as any as IRequestsToSyncItem).createdAt,
        });
        return true;
      }
    } catch (ex) {
      handleError(
        ex,
        STATE_PROCESS_ERROR,
        SYNC_PROOF_OF_PAYMENT_REPORT_REQUEST,
      );
    }
    return false;
  };

  const syncSalesOrderRequest = async (
    toSync: IRequestsToSyncItem,
  ): Promise<boolean> => {
    const token = getToken();
    try {
      const response = await apiService.postNewOrder(
        (toSync as any as IRequestsToSyncItem).request as IOrderRequest,
        token,
        false,
      );

      if (response && response.success) {
        dispatch({
          type: SYNC_SALES_ORDER_REQUEST,
          payload: (toSync as any as IRequestsToSyncItem).createdAt,
        });
        return true;
      }
    } catch (ex) {
      handleError(ex, STATE_PROCESS_ERROR, SYNC_SALES_ORDER_REQUEST);
    }
    return false;
  };

  const fetchOrderCandidates = async (): Promise<boolean> => {
    try {
      if (state.client) {
        const response: IResponse =
          await apiService.getSalesmanCandidateOrderList(
            state.client?.id,
            getToken(),
          );

        const dispatched = {
          type: FETCH_ORDER_CANDIDATES,
          payload: getSalesmanCandidateOrderList(response),
        };
        dispatch(dispatched);
        await delay(1000);
        if (response.success && dispatched.payload.length >= 0) {
          return true;
        }
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, FETCH_SCHEDULE);
    }

    return false;
  };

  const fetchCandidateOrderDetail = async (id: string): Promise<boolean> => {
    try {
      const response: IOrderCandidateResponse =
        await apiService.getSalesmanCandidateOrder(id, getToken());

      if (response.success) {
        const order = getCandidateOrderDetail(response);

        if (order && order?.priceListId && order?.paymentOptionId) {
          await fetchSalesmanProducts(
            order?.priceListId,
            order?.paymentOptionId,
          );

          const dispatched = {
            type: FETCH_CANDIDATE_ORDER_DETAIL,
            payload: getCandidateOrderDetail(response),
          };

          dispatch(dispatched);

          return true;
        }
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, GET_SALESMAN_ORDER_DETAIL);
    }
    return false;
  };

  const value = {
    ...state,
    clearErrors,
    fetchClients,
    fetchBalanceList,
    setSalesmanNewClientRequest,
    sendSalesmanNewClientRequest,
    fetchSalesmanProducts,
    setSalesmanProductToAdd,
    createSalesmanNewOrder,
    updateSalesmanNewOrder,
    updateCandidateToShoppingCart,
    sendSalesmanNewOrder,
    resetNewOrder,
    setSalesmanClient,
    fetchBalanceCustomer,
    getProfile,
    fetchBalanceSheet,
    fetchResources,
    fetchHistoryList,
    fetchSemaphoreItems,
    fetchDebtorList,
    fetchReceivableListByDebtor,
    sendProof,
    setPortfolioFilter,
    fetchSalesmanOrderList,
    fetchSalesmanOrderDetail,
    setSalesmanOrderDetail,
    setSalesmanOrderFilter,
    clearSalesmanProducts,
    fecthSchedule,
    setAppointment,
    sendAppointConfirmation,
    fetchInvoiceCandidates,
    setInvoiceCandidate,
    setInvoiceRequest,
    setInvoiceItemEditing,
    sendInvoiceRequest,
    saveRequestToServer,
    retrieveRequestToServer,
    clearRequestToServer,
    hasRequestsToSinchronize,
    syncNewClientRequest,
    updateAllRequestToStorage,
    syncPaymentProofRequest,
    syncSalesOrderRequest,
    fetchOrderCandidates,
    fetchCandidateOrderDetail,
    actions: ACTION_VERBS,
  } as IContext;

  /**
   *
   */
  return <Context.Provider value={value}>{props.children}</Context.Provider>;
};
